import React, { useState, useEffect } from 'react'

import { getUrlParameter, isPdfUrl } from '../../utility'

import { getSearchDataDiscovery } from '../../data'

import { Filter, Table, Loader, Title } from '..'

import {
  isInvestor,
  isInstitutional,
} from '../../../global/authentication/authentication-manager.js'

import { convertDate, groupBy } from './searchUtility.js'

import { sanitizeUrl } from '../../../global/formatters/sanitize-url.js'

import './index.css'

export function Search() {
  const siteParams = isInvestor()
    ? { site: 'cti-inv', count: '50' }
    : isInstitutional()
      ? { site: 'cti-inst', count: '50' }
      : { site: 'cti-adv', count: '200' }

  const IS_ADVISOR = siteParams.site === 'cti-adv' ? true : false

  const PRODUCT_CATEGORY = 'product'
  const RESOURCE_CATEGORY = 'resources'
  const LITERATURE_CATEGORY = 'literature'
  const BLOG_CATEGORY = 'blog'
  const LEADERSHIP_CATEGORY = 'leadership'
  const OTHER_CATEGORY = 'others'

  const labels = {}

  labels[PRODUCT_CATEGORY] = 'Products'
  labels[RESOURCE_CATEGORY] = 'Resources'
  labels[BLOG_CATEGORY] = 'Insights'
  labels[LEADERSHIP_CATEGORY] = 'Leadership'
  labels[OTHER_CATEGORY] = IS_ADVISOR ? 'Other' : 'Results'

  const [results, setResults] = useState([])
  const [selectedSearch, setSelectedSearch] = useState('')
  const [isFetching, setIsFetching] = useState(false)
  const [selectedFundFilter, setSelectedFundFilter] = useState('')

  const getAspect = (section) => {
    const aspects = [
      {
        name: '#search',
        info: {
          selector: [
            {
              name: 'query-results',
              isArray: false,
            },
            {
              name: 'list',
              isArray: false,
            },
          ],
          getData: getSearchDataDiscovery,
          parseData: parseData,
          processData: IS_ADVISOR ? processDataAdvisor : processData,
          categorizeData: IS_ADVISOR ? categorizeData : undefined,
          columns: [
            {
              name: 'result',
              className: 'Table-header',
              ariaLabel: 'Search Result',
              content: <>Result</>,
              cell: {
                className: !IS_ADVISOR ? 'u-size11of12' : '',
                padding: !IS_ADVISOR
                  ? 'u-paddingLeft0 u-paddingRight2gu u-paddingVert5gu'
                  : '',
                content: !IS_ADVISOR
                  ? (result) => renderResult(getItem(result))
                  : (fund) => renderItem(fund),
              },
            },
          ],
        },
      },
    ]

    return aspects.find((a) => a.name === section).info
  }

  useEffect(() => {
    const searchParamSearch = decodeURIComponent(
      sanitizeUrl(getUrlParameter('search'))
    )

    if (searchParamSearch) {
      setSelectedSearch(searchParamSearch)

      setIsFetching(true)
    }
  }, [])

  useEffect(() => {
    initializeData()
  }, [selectedSearch])

  const parseData = (list) => {
    if (list.results) {
      if (!Array.isArray(list.results)) {
        return [list.results]
      } else {
        return list.results
      }
    }

    return []
  }

  const processData = (results) => {
    let list = results.filter(
      (item) =>
        item?.metadata?.source?.url &&
        (item?.properties?.enriched_html?.[0].entities.some(
          (entity) =>
            entity.model_name === 'Title' || entity.model_name === 'Omni_Title'
        ) ||
          item?.properties?.title)
    )

    list = removePageNotFound(list)

    const uniqueTitles = new Set(list.map((a) => a.properties.title))

    return Array.from(uniqueTitles).map((title) => {
      return list.find((a) => a.properties.title === title)
    })
  }

  const processDataAdvisor = (results) => {
    const listEnrichedHtml = results.filter(
      (item) =>
        item?.metadata?.source?.url &&
        (item?.properties?.enriched_html?.[0].entities.some(
          (entity) =>
            entity.model_name === 'Title' || entity.model_name === 'Omni_Title'
        ) ||
          item?.properties?.title)
    )
    const uniqueTitlesEnrichedHtml =
      listEnrichedHtml.length > 0
        ? new Set(listEnrichedHtml.map((item) => item.properties?.title))
        : []
    const listEnrichedHtmlProcessed =
      uniqueTitlesEnrichedHtml.size > 0
        ? Array.from(uniqueTitlesEnrichedHtml).map((title) => {
            return listEnrichedHtml.find(
              (item) => item.properties?.title === title
            )
          })
        : []

    const listDocuments = results.filter((item) => item?.properties?.document)
    const uniqueTitlesDocuments =
      listDocuments.length > 0
        ? new Set(
            listDocuments.map(
              (item) =>
                item.properties?.document?.Indexedurl ||
                item.properties?.document?.Title
            )
          )
        : []
    const listDocumentsProcessed =
      uniqueTitlesDocuments.size > 0
        ? Array.from(uniqueTitlesDocuments).map((docProp) => {
            return listDocuments.find(
              (item) =>
                item.properties?.document?.Indexedurl === docProp ||
                item.properties?.document?.Title === docProp
            )
          })
        : []

    return [...listEnrichedHtmlProcessed, ...listDocumentsProcessed]
  }

  const removePageNotFound = (list) => {
    return list.filter((item) => {
      const entities = item?.properties?.enriched_html?.[0].entities

      if (entities) {
        return entities.find(
          (entity) => !entity.text.includes('Page Not Found')
        )
      }

      return []
    })
  }

  const categorizeData = (data) => {
    const productList = []
    const resourceList = []
    const blogList = []
    const leadershipList = []
    const otherList = []

    data.forEach((item) => {
      // Category is 'Other' for Investor
      const category = getContentValue(item, 'Category').toLowerCase()

      const isInsightItem = hasKey(item, 'publication_names')

      if (category.indexOf(PRODUCT_CATEGORY) >= 0) {
        item.category = PRODUCT_CATEGORY

        productList.push(item)
      } else if (
        category == RESOURCE_CATEGORY ||
        category == LITERATURE_CATEGORY
      ) {
        item.category = RESOURCE_CATEGORY

        resourceList.push(item)
      } else if (category == BLOG_CATEGORY || isInsightItem) {
        item.category = BLOG_CATEGORY

        blogList.push(item)

        if (item.source === 'columbia-auth-page') {
          item.secure = true
        }
      } else if (category == LEADERSHIP_CATEGORY) {
        item.category = LEADERSHIP_CATEGORY

        leadershipList.push(item)
      } else if (category == OTHER_CATEGORY) {
        item.category = OTHER_CATEGORY

        otherList.push(item)
      } else {
        item.category = OTHER_CATEGORY

        otherList.push(item)
      }
    })

    const resourcesListSorted = resourceList.sort(sortByFundClassFundName)

    const productListSorted = sortProducts(productList)

    // TODO - Boosted pages
    // if (root && root.vce && root.vce.boost) {
    //   root.vce.boost.reverse().forEach(item => productListSorted.unshift(item.document.spotlightcontent))
    // }

    const _results = {}

    _results[PRODUCT_CATEGORY] = productListSorted
    _results[BLOG_CATEGORY] = blogList
    _results[RESOURCE_CATEGORY] = resourcesListSorted
    _results[LEADERSHIP_CATEGORY] = leadershipList
    _results[OTHER_CATEGORY] = otherList

    return _results
  }

  const sortProducts = (productList) => {
    let sortedArray = []

    const productMap = groupBy(productList, function (item) {
      return getContentValue(item, 'fundname')
    })

    Object.keys(productMap).forEach((key) => {
      sortedArray = sortedArray.concat(productMap[key].sort(sortByFundClass))
    })

    return sortedArray
  }

  const sortByFundClass = (item1, item2) => {
    const item1FundClass = getContentValue(item1, 'fundclass')
    const item2FundClass = getContentValue(item2, 'fundclass')

    return item1FundClass < item2FundClass ? -1 : 1
  }

  const sortByFundClassFundName = (item1, item2) => {
    const item1FundClass = getContentValue(item1, 'Fundclass')
    const item2FundClass = getContentValue(item2, 'Fundclass')

    const item1Fundname = getContentValue(item1, 'Fundname')
    const item2Fundname = getContentValue(item2, 'Fundname')

    // if the fundname is the same, use the class as a tie breaker
    if (item1Fundname === item2Fundname) {
      return item1FundClass < item2FundClass ? -1 : 1
    }

    // forms show up before lit
    // if (item1.source != item2.source) {
    //     return (item1.source > item2.source) ? -1 : 1
    // }

    // return (item1.score > item2.score) ? -1 : 1
  }

  const getContentValue = (item, name) => {
    const entities = item.properties?.enriched_html?.[0]?.entities
    const document = item.properties?.document

    if (entities) {
      const content = entities.filter((entity) => entity.model_name === name)

      if (content?.[0]?.text) {
        content[0].text.replace(/<(?:.|\n)*?>/gm, '')

        return (content[0].text || '').trim()
      }

      return ''
    } else if (document) {
      return document[name] || ''
    }
  }

  const hasKey = (item, keyName) => {
    return item.properties?.enriched_html?.[0]?.entities?.some(
      (entity) => entity.model_name === keyName
    )
  }

  const initializeData = async () => {
    if (!selectedSearch) {
      return
    }

    setIsFetching(true)

    const aspect = getAspect('#search')

    const data = await aspect.getData(
      siteParams.site,
      sanitizeInput(selectedSearch),
      siteParams.count
    )

    let _results = []

    if (data) {
      if (aspect.parseData) {
        _results = aspect.parseData(data)
      }

      if (aspect.processData) {
        _results = aspect.processData(_results)
      }

      if (aspect.categorizeData) {
        _results = aspect.categorizeData(_results)
      }
    }

    // TODO
    setTimeout(() => {
      setIsFetching(false)

      setResults(_results)
    }, 1000)
  }

  const getVariable = (variable) => {
    if (variable && variable !== 'undefined') {
      return variable
    }

    return ''
  }

  const sanitizeInput = (input) => {
    let _input = input

    _input = _input.replaceAll('?', ' ')
    _input = _input.replaceAll('.', ' ')
    _input = _input.replaceAll('*', ' ')
    _input = _input.replaceAll('+', ' ')
    _input = _input.replaceAll('~', ' ')
    _input = _input.replaceAll('(', ' ')
    _input = _input.replaceAll(')', ' ')
    _input = _input.replaceAll('{', ' ')
    _input = _input.replaceAll('}', ' ')
    _input = _input.replaceAll('[', ' ')
    _input = _input.replaceAll(']', ' ')
    _input = _input.replaceAll('-', ' ')
    _input = _input.replaceAll('^', ' ')
    _input = _input.replaceAll('|', ' ')
    _input = _input.replaceAll('$', ' ')
    _input = _input.replaceAll('/', ' ')
    _input = _input.replaceAll('&', ' ')
    _input = _input.replaceAll(':', ' ')
    _input = _input.replaceAll('"', ' ')
    _input = _input.replaceAll("'", ' ')

    return _input
  }

  const sanitizeOutput = (str) => {
    let _result = str

    if (_result) {
      _result = _result.replaceAll('&amp;', '&')
      _result = _result.replaceAll('&apos;', "'")
      _result = _result.replaceAll('&lt;', '<')
      _result = _result.replaceAll('&gt;', '>')
      _result = _result.replaceAll('&quot;', '"')
      _result = _result.replaceAll('&nbsp;', ' ')
    }

    return _result
  }

  const onSearch = (event) => {
    const _value = event.currentTarget.value

    if (_value !== selectedSearch) {
      setResults([])
    }

    setSelectedFundFilter('')
    setSelectedSearch(_value)
  }

  const onClickFundFilter = (event) => {
    setSelectedFundFilter(event.currentTarget.dataset.filter)
  }

  const onClickViewAll = (event) => {
    if (selectedFundFilter === event.currentTarget.dataset.filter) {
      setSelectedFundFilter('')
    } else {
      setSelectedFundFilter(event.currentTarget.dataset.filter)
    }
  }

  const onClearSearch = () => {
    setSelectedSearch('')

    setResults([])
  }

  const getDiscoveryContentValue = (item, name) => {
    const itemProperties = item.properties?.enriched_html?.[0]?.entities
    if (itemProperties) {
      for (let i = 0; i < itemProperties.length; i++) {
        const itemContent = itemProperties[i]

        if (
          itemContent.model_name &&
          itemContent.model_name == name &&
          itemContent.text
        ) {
          itemContent.text = itemContent.text.replace(/<(?:.|\n)*?>/gm, '')

          return (itemContent.text || '').trim()
        }
      }

      return ''
    }
  }

  const getItem = (item) => {
    const url = item?.metadata?.source?.url || null

    const omniTitle =
      getDiscoveryContentValue(item, 'Omni_Title') ||
      getDiscoveryContentValue(item, 'Title') ||
      item.properties.title
    const omniDescription =
      getDiscoveryContentValue(item, 'Omni_description')?.indexOf(
        '<meta property='
      ) > 0
        ? getDiscoveryContentValue(item, 'Omni_Title') || item.properties.title
        : getDiscoveryContentValue(item, 'Omni_description') ||
          item.properties.title
    const description =
      getDiscoveryContentValue(item, 'description')?.indexOf(
        '<meta property='
      ) > 0
        ? getDiscoveryContentValue(item, 'Omni_Title') || item.properties.title
        : getDiscoveryContentValue(item, 'description') || item.properties.title

    const resultItem = {
      url: url,
      titleLine: getVariable(omniTitle),
      descriptionLine: getVariable(omniDescription) || getVariable(description),
    }

    return resultItem
  }

  const getProductTypeLine = (
    item,
    fundName,
    fundClass,
    ticker,
    productType,
    alternatetitle
  ) => {
    const isResource = item.properties?.document?.Alternatetitle

    if (isResource) {
      return (
        getVariable(fundName) +
        ' - ' +
        getVariable(fundClass) +
        ' | ' +
        getVariable(ticker) +
        ' | ' +
        getVariable(getContentValue(item, 'Producttype')) +
        ' | ' +
        getVariable(alternatetitle)
      )
    } else {
      return getVariable(productType)
    }
  }

  const getTitleLine = (item, fundName, fundClass, title) => {
    const hasDocument = item.properties?.document
    const isResource = item.properties?.document?.Alternatetitle
    const hasFundClass = item.properties?.document?.Fundclass

    if (isResource) {
      return getVariable(title)
    } else {
      return hasDocument
        ? getVariable(fundName) +
            (hasFundClass ? ' - ' : '') +
            getVariable(fundClass)
        : getVariable(title)
    }
  }

  const renderItem = (item) => {
    const hasDocument = item.properties?.document
    const isResource = item.properties?.document?.Alternatetitle
    const url =
      item.metadata?.source?.url ||
      item.properties?.document?.Indexedurl ||
      null

    const fundName = getContentValue(item, 'Fundname')
    const fundClass = getContentValue(item, 'Fundclass')
    const ticker = getContentValue(item, 'Ticker').replaceAll(';', '')
    const productTypeCode = getContentValue(item, 'Products')
    const productType = lookupProductType(productTypeCode)

    const alternatetitle = getContentValue(item, 'Alternatetitle')
    const blogDate = formatBlogDate(getContentValue(item, 'published_time'))
    const publicationType = getContentValue(item, 'publication_names')

    const title =
      getContentValue(item, 'Omni_Title') ||
      getContentValue(item, 'Title') ||
      item.properties.title
    const description = hasDocument
      ? getContentValue(item, 'Description')
      : getContentValue(item, 'description')?.indexOf('<meta property=') > 0
        ? getContentValue(item, 'Omni_Title') || item.properties.title
        : getContentValue(item, 'description') || item.properties.title
    const omniDescription =
      getContentValue(item, 'Omni_description')?.indexOf('<meta property=') > 0
        ? getContentValue(item, 'Omni_Title') || item.properties.title
        : getContentValue(item, 'Omni_description') || item.properties.title

    const resultItem = {
      url: url,
      lockImage: '',
      titleLine: getTitleLine(item, fundName, fundClass, title, alternatetitle),
      tickerLine: !isResource ? getVariable(ticker) : '',
      authorLine: hasKey(item, 'publication_names') && getVariable(blogDate),
      descriptionLine: !isResource
        ? getVariable(omniDescription) || getVariable(description)
        : getVariable(alternatetitle),
      productTypeLine: getProductTypeLine(
        item,
        fundName,
        fundClass,
        ticker,
        productType,
        alternatetitle
      ),
      publicationTypeLine: getVariable(publicationType),
    }

    return renderResultAdvisor(resultItem)
  }

  const renderResult = (item) => {
    return (
      <>
        <a
          className={'u-textSecondary u-textBold u-sm-textNone'}
          href={item.url}
          target={isPdfUrl(item.url) ? '_blank' : '_self'}
          rel="noreferrer"
        >
          {sanitizeOutput(item.titleLine)}
        </a>
        {item.descriptionLine && (
          <p className="u-marginTop2gu">
            {sanitizeOutput(item.descriptionLine)}
          </p>
        )}
      </>
    )
  }

  const renderResultAdvisor = (resultItem) => {
    return (
      <>
        <a
          className={'u-textBold u-sm-textNone' + resultItem.lockImage}
          href={resultItem.url}
          target={isPdfUrl(resultItem.url) ? '_blank' : '_self'}
          rel="noreferrer"
        >
          {sanitizeOutput(resultItem.titleLine)}
        </a>
        {resultItem.tickerLine && (
          <p className="u-marginBottom0 u-textColorLight u-textUppercase">
            {resultItem.tickerLine}
          </p>
        )}
        {resultItem.authorLine && (
          <p className="u-marginBottom0 u-textColorLight">
            {resultItem.authorLine}
          </p>
        )}
        {resultItem.descriptionLine && (
          <p className="u-marginBottom0">
            {sanitizeOutput(resultItem.descriptionLine)}
          </p>
        )}
        {resultItem.productTypeLine && (
          <p className="u-marginBottom0 u-textColorLight">
            {resultItem.productTypeLine}
          </p>
        )}
        {resultItem.publicationTypeLine && (
          <p className="u-marginBottom0 u-textColorLight">
            {resultItem.publicationTypeLine}
          </p>
        )}
      </>
    )
  }

  const lookupProductType = (productTypeCode) => {
    if (productTypeCode) {
      productTypeCode = productTypeCode.replace(/<(?:.|\n)*?>/gm, '')

      switch (productTypeCode.toLowerCase()) {
        case 'product>retail':
        case 'product>mutual fund':
          return 'Mutual Funds'
        case 'product>future scholar 529':
          return 'Future Scholar 529'
        case 'product>closed end':
          return 'Closed-End Funds'
        case 'product>variable products':
          return 'Variable Products'
        case 'product>managed accounts':
          return 'Managed Accounts'
        case 'product>retailetf':
          return 'Exchange Traded Funds'
        default:
          return ''
      }
    }
  }

  const formatBlogDate = (dateString) => {
    if (dateString) {
      dateString = dateString.replace(/<(?:.|\n)*?>/gm, '')

      const mdy = dateString.split(' ')[0]

      return convertDate(mdy)
    }

    return ''
  }

  const aspect = getAspect('#search')

  // console.log('results >>>', results)

  const resultsCount = IS_ADVISOR
    ? Object.keys(results).reduce(
        (prev, curr) => prev + results[curr].length,
        0
      )
    : results.length

  const resultsKeys = IS_ADVISOR
    ? Object.keys(results).filter((key) => results[key].length > 0)
    : 0

  return (
    <div className="u-sizeConstrained search-scope">
      <div className="u-paddingVert2gu">
        <div className="u-marginBottom10gu u-marginTop8gu">
          <Filter
            placeholder="Please enter a search term"
            defaultSearchText={selectedSearch}
            mode={'on-key-enter large-icon submit-button'}
            onSearch={onSearch}
            onClearSearch={onClearSearch}
          />
          {!resultsCount ? (
            <div className="u-padding15gu u-textColorLight u-textCenter">
              <div className="u-textSizeMsrp2">
                {isFetching ? (
                  <Loader showBackground={false} />
                ) : (
                  selectedSearch &&
                  !resultsCount && (
                    <div>
                      Sorry, no results were found for &quot;{selectedSearch}
                      &quot;. You can check the spelling, or try using a
                      different search term.
                    </div>
                  )
                )}
              </div>
            </div>
          ) : IS_ADVISOR ? (
            <div className="u-flex u-marginTop6gu">
              <div className="filter u-size1of4">
                {resultsKeys.map((key, index) => (
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- CTINEXT-4723
                  <div
                    key={key}
                    className={
                      'u-padding3gu u-textUppercase u-borderColorNeutralLight u-borderBottom u-borderTop u-borderLeft0 u-borderRight0' +
                      (index > 0 ? ' u-borderTop0' : '') +
                      (selectedFundFilter === key ? ' selected-filter' : '')
                    }
                    style={{ cursor: 'pointer' }}
                    data-filter={key}
                    onClick={onClickFundFilter}
                  >
                    {labels[key]}
                  </div>
                ))}
              </div>
              <div
                className={
                  'u-border u-borderBottom u-borderTop u-borderLeft0 u-borderRight0 u-size3of4 u-paddingLeft5gu'
                }
              >
                {resultsKeys.map(
                  (key) =>
                    (!selectedFundFilter || selectedFundFilter === key) && (
                      <div
                        key={key}
                        className="Card Content u-paddingTop3gu u-paddingBottom5gu u-marginBottom10gu"
                        data-qa-automation={`search-results-${labels[
                          key
                        ].toLowerCase()}`}
                      >
                        <div className="u-flex u-padding3gu">
                          <h2 className="u-flexAuto">{labels[key]}</h2>
                          <div>
                            <button
                              className="Link"
                              data-filter={key}
                              onClick={onClickViewAll}
                            >
                              {selectedFundFilter === key
                                ? 'Back to results'
                                : 'View all'}
                            </button>
                          </div>
                        </div>
                        <Table
                          columns={aspect.columns}
                          rows={results[key].slice(
                            0,
                            selectedFundFilter === key ? 99 : 5
                          )}
                          mode="no-header no-stripe"
                        />
                      </div>
                    )
                )}
              </div>
            </div>
          ) : (
            <div className="u-marginTop8gu u-flex u-flexCol u-sizeFull u-flexAlignItemsStart">
              <div className="u-marginBottom5gu">
                <Title
                  text="Results"
                  mode="medium-line"
                  lineColor="u-bgColorBrand"
                />
              </div>
              <Table
                columns={aspect.columns}
                rows={results.slice(0, 10)}
                mode="no-header no-stripe"
                emptyMessage=" "
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
